import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Plane, Users, Building2, Coins, Wallet, Search, ChevronDown, ChevronRight,
  FileSpreadsheet, FileText, Printer, SlidersHorizontal, Filter, Loader2,
} from "lucide-react";
import styles from "./FlightReport.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  getFlightManifest, getFlightFilterOptions, downloadFlightReportPdf,
} from "../../../api/reports/useReportsApi";
import { formatEUR, formatReportDate, downloadExcel, printHtml, buildFilterLines, filtersToPrintHtml } from "../reportUtils";

const EMPTY_SUMMARY = { flights: 0, travellers: 0, airlines: 0, revenue: 0, supplierCost: 0 };
const BOOKING_STATUSES = ["Confirmed", "Cancelled", "Completed"];
const PAGE_SIZES = [25, 50, 100];

const TYPE_FULL = { ADT: "Adult", CHD: "Child", INF: "Infant" };

// Country/nationality → ISO-3166 alpha-2 for the flag image. Covers the common
// source markets; unknowns fall back to the plain nationality text.
const COUNTRY_ISO = {
  belgium: "be", belgian: "be", "united kingdom": "gb", uk: "gb", "great britain": "gb",
  british: "gb", england: "gb", netherlands: "nl", dutch: "nl", germany: "de", german: "de",
  france: "fr", french: "fr", turkey: "tr", turkish: "tr", "türkiye": "tr", spain: "es",
  spanish: "es", italy: "it", italian: "it", portugal: "pt", portuguese: "pt", morocco: "ma",
  moroccan: "ma", luxembourg: "lu", switzerland: "ch", swiss: "ch", austria: "at", austrian: "at",
  poland: "pl", polish: "pl", greece: "gr", greek: "gr", ireland: "ie", irish: "ie",
  "united states": "us", usa: "us", american: "us", canada: "ca", canadian: "ca",
  "united arab emirates": "ae", uae: "ae", egypt: "eg", egyptian: "eg", tunisia: "tn",
  tunisian: "tn", algeria: "dz", algerian: "dz", romania: "ro", romanian: "ro", bulgaria: "bg",
  sweden: "se", norway: "no", denmark: "dk", finland: "fi", russia: "ru", russian: "ru",
  ukraine: "ua", india: "in", indian: "in", china: "cn", chinese: "cn", japan: "jp",
  brazil: "br", "south africa": "za", australia: "au", "czech republic": "cz", hungary: "hu",
  croatia: "hr", serbia: "rs", cyprus: "cy", malta: "mt", israel: "il", lebanon: "lb",
};

const isoFor = (nationality) => {
  const key = String(nationality || "").trim().toLowerCase();
  if (COUNTRY_ISO[key]) return COUNTRY_ISO[key];
  if (/^[a-z]{2}$/.test(key)) return key;
  return "";
};

const Flag = ({ nationality }) => {
  const iso = isoFor(nationality);
  return (
    <span className={styles.natCell}>
      {iso && (
        <img
          className={styles.flag}
          src={`https://flagcdn.com/20x15/${iso}.png`}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <span>{nationality || "—"}</span>
    </span>
  );
};

const KPI_META = [
  { key: "flights", label: "Flights", Icon: Plane, cls: "kpiBlue", fmt: (v) => v },
  { key: "travellers", label: "Travellers", Icon: Users, cls: "kpiGreen", fmt: (v) => v.toLocaleString("en-US") },
  { key: "airlines", label: "Airlines", Icon: Building2, cls: "kpiPurple", fmt: (v) => v },
  { key: "revenue", label: "Revenue", Icon: Coins, cls: "kpiOrange", fmt: (v) => formatEUR(v) },
  { key: "supplierCost", label: "Supplier Cost", Icon: Wallet, cls: "kpiCyan", fmt: (v) => formatEUR(v) },
];

const TypeBadge = ({ type }) => (
  <span className={`${styles.typeBadge} ${styles[`type${type}`] || styles.typeADT}`}>
    {TYPE_FULL[type] || type}
  </span>
);

const AirlineMark = ({ name, logo }) => {
  const [failed, setFailed] = useState(false);
  const initials = String(name || "?").trim().slice(0, 2).toUpperCase();
  if (logo && !failed) {
    return <img className={styles.airlineLogo} src={logo} alt="" onError={() => setFailed(true)} />;
  }
  return <span className={styles.airlineFallback}>{initials}</span>;
};

const FlightGroup = ({ flight, open, onToggle }) => (
  <div className={`${styles.flightRow} ${open ? styles.flightRowOpen : ""}`}>
    <button className={styles.flightHead} onClick={onToggle} type="button">
      <div className={styles.fAirline}>
        <AirlineMark name={flight.airlineName} logo={flight.airlineLogo} />
        <div className={styles.fAirlineText}>
          <span className={styles.fAirlineName}>{flight.airlineName}</span>
          <span className={styles.fFlightNo}>{flight.flightNumber}</span>
        </div>
      </div>

      <div className={styles.fRoute}>
        <div className={styles.fEndpoint}>
          <span className={styles.fCode}>{flight.departureAirport || "—"}</span>
          <span className={styles.fCity}>{flight.departureCity || ""}</span>
        </div>
        <span className={styles.fArrow}>→</span>
        <div className={styles.fEndpoint}>
          <span className={styles.fCode}>{flight.arrivalAirport || "—"}</span>
          <span className={styles.fCity}>{flight.arrivalCity || ""}</span>
        </div>
      </div>

      <div className={styles.fCol}>
        <span className={styles.fColLabel}>Departure</span>
        <span className={styles.fColValue}>
          {formatReportDate(flight.departureDate)}{flight.departureTime ? ` · ${flight.departureTime}` : ""}
        </span>
      </div>
      <div className={styles.fCol}>
        <span className={styles.fColLabel}>Bookings</span>
        <span className={styles.fColValue}>{flight.bookings}</span>
      </div>
      <div className={styles.fCol}>
        <span className={styles.fColLabel}>Travellers</span>
        <span className={styles.fColValue}>{flight.travellers}</span>
      </div>
      <div className={styles.fColRight}>
        <span className={styles.fColLabel}>Revenue</span>
        <span className={styles.fColValueStrong}>{formatEUR(flight.revenue)}</span>
      </div>
      <div className={styles.fColRight}>
        <span className={styles.fColLabel}>Supplier Cost</span>
        <span className={styles.fColValue}>{formatEUR(flight.supplierCost)}</span>
      </div>
      <span className={styles.fChevron}>{open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</span>
    </button>

    {open && (
      <div className={styles.paxWrap}>
        <table className={styles.paxTable}>
          <thead>
            <tr>
              <th className={styles.pNo}>#</th>
              <th>Booking No</th>
              <th>Passenger</th>
              <th>Traveller Type</th>
              <th>Nationality</th>
              <th>Date of Birth</th>
            </tr>
          </thead>
          <tbody>
            {flight.passengers.map((p, i) => (
              <tr key={`${p.bookingReference}-${i}`}>
                <td className={styles.pNo}>{i + 1}</td>
                <td className={styles.pRef}>{p.bookingReference}</td>
                <td className={styles.pName}>{p.name}</td>
                <td><TypeBadge type={p.travelerType} /></td>
                <td><Flag nationality={p.nationality} /></td>
                <td>{formatReportDate(p.dateOfBirth)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const FlightReport = () => {
  const { showToast } = useToast();

  const [draft, setDraft] = useState({
    dateFrom: "", dateTo: "", airline: "", departureAirport: "",
    arrivalAirport: "", supplier: "", bookingStatus: "Confirmed",
  });
  const [applied, setApplied] = useState(null);
  const [options, setOptions] = useState({ airlines: [], airports: [], suppliers: [] });
  const [flights, setFlights] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(() => new Set());

  const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const runReport = useCallback(async (filters, { announce = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const res = await getFlightManifest(filters);
      if (!res?.success) throw new Error(res?.message || "Failed to generate flight report");
      const list = Array.isArray(res.data?.flights) ? res.data.flights : [];
      setFlights(list);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      setApplied(filters);
      setPage(1);
      setExpanded(new Set(list.length ? [list[0].key] : []));
      if (announce) showToast(`Report generated — ${list.length} flight departure${list.length === 1 ? "" : "s"}`, "success");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to generate flight report";
      setError(msg);
      setFlights([]);
      setSummary(EMPTY_SUMMARY);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Load dropdown options + an initial report on mount. Guarded so React 18
  // StrictMode's double-invoke doesn't fire two reports.
  const bootRef = useRef(false);
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    getFlightFilterOptions()
      .then((res) => { if (res?.success) setOptions(res.data || { airlines: [], airports: [], suppliers: [] }); })
      .catch(() => { /* dropdowns just stay empty; report still works */ });
    runReport({ ...draft });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = () => runReport({ ...draft }, { announce: true });

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // ── Client-side search over the generated flights ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flights;
    return flights.filter((f) => {
      if (
        String(f.airlineName).toLowerCase().includes(q) ||
        String(f.flightNumber).toLowerCase().includes(q) ||
        String(f.route).toLowerCase().includes(q) ||
        String(f.departureCity).toLowerCase().includes(q) ||
        String(f.arrivalCity).toLowerCase().includes(q)
      ) return true;
      return f.passengers.some(
        (p) => String(p.name).toLowerCase().includes(q) || String(p.bookingReference).toLowerCase().includes(q)
      );
    });
  }, [flights, search]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);
  const rangeFrom = total === 0 ? 0 : start + 1;
  const rangeTo = Math.min(start + pageSize, total);

  // ── Exports ──
  const exportRows = () =>
    filtered.flatMap((f) =>
      f.passengers.map((p) => [
        f.airlineName, f.flightNumber, f.route,
        `${formatReportDate(f.departureDate)}${f.departureTime ? " " + f.departureTime : ""}`,
        p.bookingReference, p.name, TYPE_FULL[p.travelerType] || p.travelerType,
        p.nationality || "", formatReportDate(p.dateOfBirth),
      ])
    );
  const EXPORT_HEADERS = [
    "Airline", "Flight No", "Route", "Departure", "Booking No", "Passenger",
    "Traveller Type", "Nationality", "Date of Birth",
  ];

  // Applied criteria + active search, surfaced in every generated export.
  const activeFilterLines = () => {
    const a = applied || draft;
    const airName = options.airlines.find((x) => x.code === a.airline)?.name;
    const supName = options.suppliers.find((s) => String(s.id) === String(a.supplier))?.name || a.supplier;
    const aptLabel = (code) => {
      const ap = options.airports.find((x) => x.code === code);
      return ap ? `${code}${ap.city ? " — " + ap.city : ""}` : code;
    };
    return buildFilterLines([
      ["Departure from", a.dateFrom ? formatReportDate(a.dateFrom) : ""],
      ["Departure to", a.dateTo ? formatReportDate(a.dateTo) : ""],
      ["Airline", a.airline ? (airName ? `${airName} (${a.airline})` : a.airline) : ""],
      ["Departure airport", a.departureAirport ? aptLabel(a.departureAirport) : ""],
      ["Arrival airport", a.arrivalAirport ? aptLabel(a.arrivalAirport) : ""],
      ["Supplier", a.supplier ? supName : ""],
      ["Booking status", a.bookingStatus],
      ["Search", search],
    ]);
  };

  const handleExcel = () => {
    if (!filtered.length) return showToast("Nothing to export", "error");
    try {
      downloadExcel("flight-report.xls", EXPORT_HEADERS, exportRows(), "Flight Report — Passenger Manifest", activeFilterLines());
      showToast("Excel exported", "success");
    } catch {
      showToast("Excel export failed", "error");
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadFlightReportPdf(applied || draft);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "flight-report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("PDF exported", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "PDF export failed", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => {
    if (!filtered.length) return showToast("Nothing to print", "error");
    const kpis = KPI_META.map(
      (k) => `<div class="kpi"><div class="l">${k.label}</div><div class="v">${k.fmt(summary[k.key] ?? 0)}</div></div>`
    ).join("");
    const sections = filtered
      .map((f) => {
        const rows = f.passengers
          .map(
            (p, i) =>
              `<tr><td>${i + 1}</td><td>${p.bookingReference}</td><td>${p.name}</td><td>${TYPE_FULL[p.travelerType] || p.travelerType}</td><td>${p.nationality || "—"}</td><td>${formatReportDate(p.dateOfBirth)}</td></tr>`
          )
          .join("");
        return `<h3 style="margin:16px 0 6px">${f.airlineName} ${f.flightNumber} — ${f.route} — ${formatReportDate(f.departureDate)}${f.departureTime ? " " + f.departureTime : ""}</h3>
          <table><thead><tr><th>#</th><th>Booking No</th><th>Passenger</th><th>Type</th><th>Nationality</th><th>Date of Birth</th></tr></thead><tbody>${rows}</tbody></table>`;
      })
      .join("");
    const ok = printHtml(
      `<h1>Flight Report</h1><p class="sub">Flight departures with passenger list · Generated ${new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}</p>${filtersToPrintHtml(activeFilterLines())}<div class="kpis">${kpis}</div>${sections}`,
      "Flight Report"
    );
    if (!ok) showToast("Please allow pop-ups to print", "error");
  };

  const pageNumbers = useMemo(() => {
    const nums = [];
    const max = pageCount;
    const cur = safePage;
    const win = 5;
    let s = Math.max(1, cur - Math.floor(win / 2));
    let e = Math.min(max, s + win - 1);
    s = Math.max(1, e - win + 1);
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  }, [pageCount, safePage]);

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerIcon}><Plane size={26} /></div>
        <div>
          <h1 className={styles.headerTitle}>Flight Report</h1>
          <p className={styles.headerSub}>View and export flight departures with passenger list.</p>
        </div>
      </div>

      {/* ── Report Criteria ── */}
      <div className={styles.card}>
        <div className={styles.criteriaHead}><Filter size={16} /> Report Criteria</div>
        <div className={styles.criteriaGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Departure Date From</span>
            <input type="date" className={styles.input} value={draft.dateFrom} onChange={setField("dateFrom")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Departure Date To</span>
            <input type="date" className={styles.input} value={draft.dateTo} onChange={setField("dateTo")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Airline</span>
            <select className={styles.select} value={draft.airline} onChange={setField("airline")}>
              <option value="">All Airlines</option>
              {options.airlines.map((a) => (
                <option key={a.code} value={a.code}>{a.name}{a.code ? ` (${a.code})` : ""}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Departure Airport</span>
            <select className={styles.select} value={draft.departureAirport} onChange={setField("departureAirport")}>
              <option value="">All Airports</option>
              {options.airports.map((a) => (
                <option key={a.code} value={a.code}>{a.code}{a.city ? ` — ${a.city}` : ""}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Arrival Airport</span>
            <select className={styles.select} value={draft.arrivalAirport} onChange={setField("arrivalAirport")}>
              <option value="">All Airports</option>
              {options.airports.map((a) => (
                <option key={a.code} value={a.code}>{a.code}{a.city ? ` — ${a.city}` : ""}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Supplier</span>
            <select className={styles.select} value={draft.supplier} onChange={setField("supplier")}>
              <option value="">All Suppliers</option>
              {options.suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Booking Status</span>
            <select className={styles.select} value={draft.bookingStatus} onChange={setField("bookingStatus")}>
              <option value="">All</option>
              {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className={styles.generateWrap}>
            <button className={styles.generateBtn} onClick={handleGenerate} disabled={loading} type="button">
              {loading ? <Loader2 size={16} className={styles.spin} /> : <Search size={16} />}
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI Summary ── */}
      <div className={styles.kpiGrid}>
        {KPI_META.map(({ key, label, Icon, cls, fmt }) => (
          <div key={key} className={`${styles.kpiCard} ${styles[cls]}`}>
            <span className={styles.kpiIcon}><Icon size={22} /></span>
            <div className={styles.kpiText}>
              <span className={styles.kpiLabel}>{label}</span>
              <span className={styles.kpiValue}>{loading ? "—" : fmt(summary[key] ?? 0)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Results ── */}
      <div className={styles.card}>
        <div className={styles.resultsHead}>
          <div className={styles.resultsTitleWrap}>
            <div className={styles.resultsTitle}><SlidersHorizontal size={16} /> Results</div>
            <div className={styles.resultsMeta}>
              {loading ? "Loading…" : `Showing ${rangeFrom} to ${rangeTo} of ${total} flight departure${total === 1 ? "" : "s"}`}
            </div>
          </div>
          <div className={styles.searchBox}>
            <Search size={15} />
            <input
              className={styles.searchInput}
              placeholder="Search flight, airline, route, passenger…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {error ? (
          <div className={styles.errorBox}>{error}</div>
        ) : loading ? (
          <div className={styles.skeletonWrap}>
            {[...Array(4)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
          </div>
        ) : paged.length === 0 ? (
          <div className={styles.empty}>
            <Plane size={30} />
            <div className={styles.emptyTitle}>No flight departures found</div>
            <div className={styles.emptySub}>Adjust the criteria above and click <b>Generate Report</b>.</div>
          </div>
        ) : (
          <div className={styles.flightList}>
            {paged.map((f) => (
              <FlightGroup key={f.key} flight={f} open={expanded.has(f.key)} onToggle={() => toggleExpand(f.key)} />
            ))}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className={styles.pagination}>
            <div className={styles.rowsPer}>
              <span>Rows per page:</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className={styles.rowsSelect}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className={styles.pager}>
              <button className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
              <button className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>‹</button>
              {pageNumbers.map((n) => (
                <button key={n} className={`${styles.pageBtn} ${n === safePage ? styles.pageBtnActive : ""}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(safePage + 1)}>›</button>
              <button className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(pageCount)}>»</button>
            </div>
            <div className={styles.showingText}>Showing {rangeFrom} to {rangeTo} of {total} flight departures</div>
          </div>
        )}
      </div>

      {/* ── Export Report ── */}
      <div className={styles.exportCard}>
        <div className={styles.exportLeft}>
          <div className={styles.exportIcon}><FileText size={18} /></div>
          <div>
            <div className={styles.exportTitle}>Export Report</div>
            <div className={styles.exportSub}>Export the flight report in your preferred format.</div>
          </div>
        </div>
        <div className={styles.exportBtns}>
          <button className={`${styles.exportBtn} ${styles.excelBtn}`} onClick={handleExcel} disabled={loading} type="button">
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          <button className={`${styles.exportBtn} ${styles.pdfBtn}`} onClick={handlePdf} disabled={loading || pdfLoading} type="button">
            {pdfLoading ? <Loader2 size={16} className={styles.spin} /> : <FileText size={16} />} Export PDF
          </button>
          <button className={`${styles.exportBtn} ${styles.printBtn}`} onClick={handlePrint} disabled={loading} type="button">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightReport;
